import _ from 'lodash'
import AuthMixin from '@/components/mixins/auth-mixin'
import EventBus from '@/components/mixins/event-bus-mixin'
import MenuItems from '@/components/common/menu-items/menu-items-tmpl'
import UnsavedDataMixin from '@/components/mixins/unsaved-data-mixin'
import UserAccountTopSettings from '@/components/common/user-account-top-settings/user-account-top-settings-tmpl'
import HelpCenterButton from '@/components/common/help-center-button/help-center-button-tmpl'
import { useCognitoAuthStore } from '@base-shared/stores/cognito-auth';
import { useUserStore } from '@base-shared/stores/user';
import { useGroupStore } from '@base-shared/stores/group';

export default {
  name: 'navigation',
  inject: [
    '$currentUser',
    '$utils',
    '$dataStore',
    '$api',
    '$apiError',
    '$signedInUserInfo',
    '$signedInUserAccounts'
  ],
  compatConfig: {
    MODE: 3 // opt-in to Vue 3 behavior for this component only,
  },
  components: {
    menuItemsComp: MenuItems,
    UserAccountTopSettings,
    HelpCenterButton
  },
  watch: {
    '$route' (routeTo) {
      this.isDistributorLinkActive = false

      if (routeTo.name === 'distributor') this.isDistributorLinkActive = true

      this.resetAppLogo()
      this.setAppLogo()
    },
    /**
     * Watches the `userStore.currentUser` for changes. Replacement
     * for the old `BIND_USER_DETAILS_IN_VUE` from EventBus.
     */
    'userStore.currentUser': {
      handler (newValue) {
        const firstName = newValue?.firstName ?? '';
        const lastName = newValue?.lastName ?? '';
        this.userControlText = firstName || lastName
          ? `${firstName.charAt(0)}. ${lastName}`
          : '';
      },
      deep: true
    },
    /**
     * Watches the `userStore.accounts` for changes. Replacement
     * for the old `BIND_USER_ACCOUNTS_IN_VUE` from EventBus.
     */
    'userStore.accounts': {
      handler (newValue) {
        this.setUserData()
      },
      deep: true
    },
    /**
     * Watches the `groupStore.groupInfo` for changes. Replacement
     * for the old `USER_INFO_AVAILABLE_IN_VUE` from EventBus.
     */
    'groupStore.groupInfo': {
      handler (newValue) {
        this.setUserData()
      },
      deep: true
    }
  },
  computed: {
    isAuthenticated () {
      return useCognitoAuthStore().isAuthenticated
    }
  },
  setup () {
    // Expose the user store to the Options API context.
    return {
      userStore: useUserStore(),
      groupStore: useGroupStore()
    };
  },
  data () {
    return {
      isVisible: false,
      title: 'Navigation',
      selectedLanguage: { code: '', name: '' },
      isDistributorLinkVisible: false,
      timer: null,
      isDistributorLinkActive: false,
      isUserCreateLocked: this.$currentUser.getRights().userCreateLock,
      userDbListForSmatText: {
        fetchDone: false,
        userDb: []
      },
      appLogo: {
        url: '/static/images/logo-64.png',
        type: 'spinetix',
        link: null,
        show: false
      },
      FAQLink: null,
      userControlText: '',
      isUserControlVisible: false,
      isUserControlsActive: false,
      userInfo: null,
      userAccounts: [],
      hasUserAccountsAndDemoVersion: false,
      isLanguageSelectorVisible: false,
      isHubEnabled: CONFIG.HAS_HUB
    }
  },
  mixins: [AuthMixin, UnsavedDataMixin],
  methods: {
    setUserData () {
      this.userInfo = this.$signedInUserInfo
      this.userAccounts = this.$signedInUserAccounts

      if (this.userAccounts.length > 1 && (this.userInfo && this.userInfo.activePlan.name === 'enterprise-demo')) {
        this.hasUserAccountsAndDemoVersion = true
      } else {
        this.hasUserAccountsAndDemoVersion = false
      }
    },
    handleUserDetailsSelection (event) {
      if (event === 'signOut') {
        this.signOut()
      }
    },
    handleNavigationVisibility () {
      const that = this

      // Listen from Global Event Bus
      EventBus.$on('SIGNED_IN', (payLoad) => {
        that.isVisible = payLoad.mainNavVisible
        that.isDistributorLinkVisible = false
        this.isUserControlVisible = true

        // Distributor button visibility
        if (this.$utils.isPageForLicenses()) {
          if (this.$currentUser.hasGroupDistributor()) {
            this.isDistributorLinkVisible = true
          }
        } else {
          // NOTE: relevant only for ARYA
          this.getUserDbForSmartText({ signedIn: true })
        }
      })

      // Check Route name
      const routeToHideNavigation = ['sign-in', 'sign-up', 'forgot-password']
      if (routeToHideNavigation.indexOf(that.$route.name) < 0) {
        that.isVisible = true
      }

      if (that.$route.name === 'distributor') that.isDistributorLinkActive = true

      // On signout
      EventBus.$on('SIGNED_OUT', (payLoad) => {
        this.isUserControlVisible = false
        that.isDistributorLinkVisible = false
        this.userDbListForSmatText = { fetchDone: false, userDb: [] }
        clearTimeout(this.timer)
      })
    },
    setLanguage () {
      const selectedLanguage = this.$dataStore.getLanguage()
      this.selectedLanguage = { code: selectedLanguage.code, name: selectedLanguage.name }
    },
    setAppLogo () {
      if (!this.isAuthenticated) {
        const appLogo = this.$utils.clone(this.appLogo)
        appLogo.show = true
        this.appLogo = appLogo
      }

      this.resetAppLogo()
      this.appLogo.show = true
    },
    resetAppLogo () {
      const tosStatus = this.getTermsOfServicesStatus()
      const appLogo = this.$utils.clone(this.appLogo)
      // Show Hub logo when hub is enabled, user is signed in
      // and terms and services is not visible or required
      if (
        this.isHubEnabled &&
        this.isAuthenticated &&
        (!tosStatus?.visible && tosStatus?.type !== 'required' && this.$route.name !== 'tos')
      ) {
        appLogo.type = 'hub'
        appLogo.url = 'logo:hub-settings'
        appLogo.show = false
        this.appLogo = appLogo
        return;
      }

      // Reset to default or white label logo
      appLogo.type = 'spinetix'
      appLogo.url = '/static/images/logo-64.png'
      appLogo.show = false
      this.appLogo = appLogo
    },
    handleAppLogoClick () {
      const tosStatus = this.getTermsOfServicesStatus()
      // Check terms and services
      if (tosStatus?.visible || tosStatus?.type === 'required') {
        // Do nothing if terms and services is visible
        return
      } else if (this.isHubEnabled && this.isAuthenticated) {
        // Emit product selection toggle when hub is enabled
        this.$emit('toggle-product-selection');
        return
      }

      // Check unsaved data
      this.checkUnsavedData({
        nextAction: () => {
          clearTimeout(this.timer)

          if (this.isAuthenticated) {
            if (this.$utils.isPageForLicenses()) {
              this.$router.push('/activation', () => {})
            } else {
              if (!_.isEmpty(this.$signedInUserInfo._edit) && !_.isEmpty(this.$signedInUserInfo._edit.plan.currentPlan) && this.$signedInUserInfo._edit.plan.currentPlan === this.$utils.options.plans.DISCOVERY) {
                this.$router.push('/plans', () => {})
              } else {
                this.$router.push('/menu', () => {})
              }
            }
          } else {
            this.$router.push('/home', () => {})
          }
        }
      })
    },
    async getUserDbForSmartText (params) {
      // Since this is called from mounted, we need to check user session from amplify instead of local storage to avoid race condition
      await useCognitoAuthStore().fetchAndStoreAuthSession()
      if (!this.isAuthenticated) return;

      const isSignedInAndFetchDone = params.signedIn && this.userDbListForSmatText.fetchDone

      if (isSignedInAndFetchDone) return

      const apiRequest = () => {
        this.$api.get(`${CONFIG.API_BASE_URL}/userdb/`)
          .then(response => {
            EventBus.$emit('USERDB_FETCHED', { fetchDone: true, userDb: response.data })
            this.userDbListForSmatText = { fetchDone: true, userDb: response.data }
          })
          .catch((error) => {
            this.userDbListForSmatText = { fetchDone: true, userDb: [] }
            this.$apiError.handleAPIRequestError(error, { originalRequest: () => { apiRequest() } })
          })
      }
      apiRequest()
    },
    handleDistributorButtonClick () {
      this.$router.push('/distributor', () => {})
    },
    handleUserControlsCommand (command) {
      if (command === 'userProfileClicked') {
        if (this.$utils.isPageForLicenses()) {
          this.$router.push('/act-user-profile')
        } else {
          this.$router.push('/user-profile')
        }
      } else if (command === 'selectLanguage') {
        EventBus.$emit('SHOW_LANGUAGE_SELECTOR', {})
        this.isLanguageSelectorVisible = true
      } else if (command === 'userSignOutClicked') {
        this.checkUnsavedData({
          nextAction: () => {
            this.signOut()
          }
        })
      }
    },
    handleUserControlsVisibleChanged (isVisible) {
      if (isVisible) {
        this.isUserControlsActive = true
      } else {
        this.isUserControlsActive = false
      }
    },
    validateUserSession () {
      EventBus.$on('REFRESH_USER_SESSION', () => {
        console.log('EventBus.REFRESH_USER_SESSION')
        this.refreshUserSession({
          hideLoader: true,
          nextAction: () => {
            EventBus.$emit('USER_SESSION_UPDATED')
          }
        })
      })
    },
  },
  created () {
    /**
     * Registering user session validator
     */
    this.validateUserSession()
  },
  async mounted () {
    await useCognitoAuthStore().fetchAndStoreAuthSession()
    this.handleNavigationVisibility()
    this.setLanguage()
    this.setAppLogo()
    this.$utils.animate('#navAppLogoWrapper', 'animated fadeInLeft')
    this.$utils.animate('animated fadeInRight')

    if (!this.$utils.isPageForLicenses()) {
      this.getUserDbForSmartText({ mounted: true })
    }

    // Listen SignedIn from Global Event Bus
    EventBus.$on('SIGNED_IN', (payload) => {
      this.setUserData()
    })

    // Listen to hide language selector dialog
    EventBus.$on('HIDE_LANGUAGE_SELECTOR', (payload) => {
      this.isLanguageSelectorVisible = false
    })
  }
}
